import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BACKEND_URL } from "../config";
import axios from "axios";

export const domainsUrl = `${BACKEND_URL}/api/v1/domains`;
const domainsTransferUrl = `${BACKEND_URL}/api/v1/domain-transfer`;
const domainSubscriptionUrl = `${BACKEND_URL}/api/v1/domain-subscriptions`;

export type DomainOut = {
    id: 0;
    name: string;
    transfer_failed: boolean;
    status: string;
    domain_data: {
        domain: string;
        owner: string;
        expire_at: string;
        id: number;
        status: string;
        zone_id: number;
        ns1: string;
        ns2: string;
        ns3: string;
        ns4: string;
        ns5: string;
    };
    handle: {
        default: boolean;
        id: number;
    };
    has_hosting: boolean;
    subscription_data: {
        status: string;
        ends_at: string;
    };
};

export type NameServerConfig = {
    ns1: string;
    ns2: string;
    ns3: string;
    ns4: string;
    ns5: string;
};

const listDomains = () => {
    return axios.request({
        url: domainsUrl,
        withCredentials: true,
        method: "GET",
    });
};

export const useListDomains = () => {
    return useQuery({
        queryKey: [domainsUrl],
        queryFn: async () => {
            const response = await listDomains();
            return response.data;
        },
        retry: 3,
    });
};

const postNameserver = (data: NameServerConfig, domainId: number) => {
    return axios.request({
        url: `${domainsUrl}/${domainId}/nameserver`,
        withCredentials: true,
        method: "PUT",
        data: data,
    });
};

export const usePutNameserver = (domainId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [],
        mutationFn: async (data: NameServerConfig) => {
            const response = await postNameserver(data, domainId);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [domainsUrl], refetchType: "all" });
        },
    });
};

const getAuthCode = (url: string) => {
    return axios.request({
        url: url,
        withCredentials: true,
        method: "GET",
    });
};

export const useGetAuthCode = (domainId: number, enabled: boolean) => {
    const url = `${domainsUrl}/${domainId}/authCode`;

    return useQuery({
        queryKey: [url],
        queryFn: async () => {
            const response = await getAuthCode(url);
            return response.data;
        },
        staleTime: 0,
        enabled: enabled,
    });
};

const unsubscribeDomain = (url: string) => {
    return axios.request({
        url: url,
        withCredentials: true,
        method: "DELETE",
    });
};

export const useUnsubscribeDomain = (domainId: number) => {
    const url = `${domainSubscriptionUrl}/${domainId}`;
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [url],
        mutationFn: async () => {
            const response = await unsubscribeDomain(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [domainsUrl] });
        },
    });
};

const uncancelDomain = (url: string) => {
    return axios.request({
        url: url,
        withCredentials: true,
        method: "POST",
    });
};

export const useUncancelDomain = (domainId: number) => {
    const url = `${domainSubscriptionUrl}/${domainId}`;
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [url],
        mutationFn: async () => {
            const response = await uncancelDomain(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [domainsUrl] });
        },
    });
};

const deleteDomain = (url: string) => {
    return axios.request({
        url: url,
        withCredentials: true,
        method: "DELETE",
    });
};

export const useDeleteDomain = (domainId: number) => {
    const url = `${domainsUrl}/${domainId}`;
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [url],
        mutationFn: async () => {
            const response = await deleteDomain(url);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [domainsUrl] });
        },
    });
};

const transferDomain = (url: string, authCode: string) => {
    return axios.request({
        url: url,
        data: { auth_code: authCode },
        withCredentials: true,
        method: "POST",
    });
};

export const useTransferDomain = (domainId: number) => {
    const url = `${domainsTransferUrl}/${domainId}`;
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [url],
        mutationFn: async (authCode: string) => {
            const response = await transferDomain(url, authCode);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [domainsUrl] });
        },
    });
};

const requestAuthCode = async (url: string, domain: string) => {
    return axios.request({
        url: url,
        data: { domain: domain },
        withCredentials: true,
        method: "POST",
    });
};

export const useRequestAuthCode = (domain: string) => {
    const url = `${domainsUrl}/auth2`;

    return useMutation({
        mutationKey: [url],
        mutationFn: async () => {
            const response = await requestAuthCode(url, domain);
            return response.data;
        },
    });
};
